import { Children, useState, useEffect, useDeferredValue } from "react";
import { useMutation } from "@tanstack/react-query";
import { useSelector, useDispatch } from "react-redux";
import {
  addSingleTask,
  deleteSingleTask,
  getTemplateLibraryTasks,
  getTemplateTasks,
  addTemplateLibraryTask,
  updateSingleTask,
  removeTemplateLibraryTask,
} from "redux/reducers/templateLibrary/tasks";
import {
  addTask,
  deleteTask,
  useGetWpslTasks,
  addTemplateTask,
  updateTask,
  removeDefaultTask,
  useGetTasks,
} from "api/templateLibrary/tasks";
import { useNotification } from "notification";
import { useTags } from "api/siteSettings/Tags";
import "../library.css";
import { AddIcon, TaskIcon } from "allIcons";
import {
  PopOver,
  Loader,
  LoadingListCards,
  BodyContainer,
  HeaderContainer,
  InputField,
  ListCard,
  ListIconWrapper,
  MainButton,
  RightMainContainer,
  PrimaryHeading,
  TemplateNotFound,
  AddFromTemplateLibrary,
  FiltersList,
} from "commonUI";
import { isEqual, parse, search } from "helpers";
import {
  libraryItemFiltering,
  libraryItemSorting,
} from "filterAndSort/templateLibrary";
import { useUserPermissions } from "hooks/user";
import { t } from "i18next";
import { ModulePrefixes } from "enums";

export default function Task() {
  const { data, isLoading, isRefetching } = useGetWpslTasks();
  const { data: wpslData } = useGetTasks();
  const [filterData, setFilterData] = useState({});
  const notification = useNotification();
  const dispatch = useDispatch();
  const { tasks, loaded, templateLibrary } = useSelector(
    (state) => state.siteTasks
  );
  const { data: tags } = useTags();
  const [searchText, setSearchText] = useState("");
  const defferedSearch = useDeferredValue(searchText);
  const [searchResult, setSearchResult] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(false);
  const { remove_items_from_site_template, edit_templates } =
    useUserPermissions();

  useEffect(() => {
    if (data && !templateLibrary.loaded && !isLoading && !isRefetching)
      dispatch(getTemplateLibraryTasks(data.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoading,
    isRefetching,
    data,
    templateLibrary.loaded,
    getTemplateLibraryTasks,
  ]);

  useEffect(() => {
    if (wpslData && !loaded) dispatch(getTemplateTasks(wpslData.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, wpslData]);

  useEffect(() => {
    setSearchResult(
      templateLibrary?.tasks?.filter((task) =>
        search(task.title, defferedSearch)
      ) || []
    );
  }, [defferedSearch, templateLibrary.tasks]);

  const addMutation = useMutation(addTask, {
    onSuccess: (data) => {
      notification({
        type: "success",
        message: t("libraryTemplate.tasks.api.success.dupilcated"),
      });
      dispatch(addSingleTask(data.data));
    },
    onError: (error) => {
      console.log(error);
    },
  });
  const deleteMutation = useMutation(deleteTask, {
    onSuccess: (data) => {
      notification({
        type: "success",
        message: t("libraryTemplate.tasks.api.success.deleted"),
      });
      dispatch(deleteSingleTask(data.id));
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const removeTaskMutation = useMutation(removeDefaultTask, {
    onSuccess: (data) => {
      notification({
        type: "success",
        message: t("libraryTemplate.tasks.api.success.removed"),
      });
      dispatch(removeTemplateLibraryTask(data.id));
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const updateMutation = useMutation(updateTask, {
    onSuccess: (data) => {
      notification({
        type: "success",
        message: t("libraryTemplate.tasks.api.success.updated"),
      });
      dispatch(updateSingleTask(data.data));
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const applySorting = (order, orderBy) => {
    setSearchResult((prevSearchResult) =>
      libraryItemSorting(order, orderBy, prevSearchResult)
    );
  };

  const applyFilter = ({
    selectedFilter,
    selectedTags,
    selectedLibrarySource,
    ...data
  }) => {
    setFilterData({
      ...data,
      selectedTags,
      selectedLibrarySource,
      selectedFilter,
    });
    setSearchResult(
      libraryItemFiltering(
        selectedFilter,
        selectedTags,
        selectedLibrarySource,
        templateLibrary.tasks
      )
    );
  };

  const addTaskMutation = useMutation(addTemplateTask, {
    onSuccess: (data) => {
      notification({
        type: "SUCCESS",
        message: t("libraryTemplate.tasks.api.success.added"),
      });
      dispatch(
        addTemplateLibraryTask(
          tasks.filter((item) => isEqual(item.id, data.data.id))[0]
        )
      );
    },
    onError: (error) => {
      console.log(error);
    },
  });

  return (
    <>
      <RightMainContainer>
        {(addMutation.isLoading ||
          deleteMutation.isLoading ||
          updateMutation.isLoading ||
          addTaskMutation.isLoading) && <Loader loader="block" />}
        <HeaderContainer>
          <PrimaryHeading>{t("libraryTemplate.tasks.title")}</PrimaryHeading>
          <div className="relative">
            <MainButton onClick={() => setOpenDropdown(!openDropdown)}>
              <AddIcon />
              {t("button.add")}
            </MainButton>
            <PopOver open={openDropdown} onClose={() => setOpenDropdown(false)}>
              <AddFromTemplateLibrary
                heading={t("common.addFromTemplateLibraryTitle")}
                data={tasks
                  .filter((item) => !isEqual(item.default_tem, 1))
                  .map((item) => ({
                    ...item,
                    onClick: () =>
                      addTaskMutation.mutate({
                        id: item.id,
                        requested_by: parse(item.requested_from),
                      }),
                    icon: <TaskIcon fill="white" />,
                    secondaryData: isEqual(item.system_status, 1)
                      ? t("module.sourceType.system")
                      : "",
                  }))}
                emptyTemplate={{
                  label: t("libraryTemplate.tasks.title"),
                  link: `/${ModulePrefixes.TEMPLATE_LIBRARY}/tasks/add`,
                  linkLabel: t("libraryTemplate.task"),
                }}
              />
            </PopOver>
          </div>
        </HeaderContainer>
        <BodyContainer>
          <InputField
            placeholder={t("input.search.placeholder")}
            searchIcon={true}
            filter={true}
            value={searchText}
            setValue={(val) => setSearchText(val)}
            tags={tags ?? []}
            onApplyFilter={applyFilter}
            applySorting={applySorting}
            filterData={filterData}
            showLibrarySource={false}
          />
          <FiltersList filterData={filterData} updateFilter={applyFilter} />
          {isLoading ? (
            <LoadingListCards />
          ) : templateLibrary.tasks.length === 0 ||
            searchResult.length === 0 ? (
            <TemplateNotFound
              label={t("libraryTemplate.tasks.title")}
              linkLabel={t("libraryTemplate.task")}
              onClick={() => setOpenDropdown(!openDropdown)}
            />
          ) : (
            Children.toArray(
              searchResult.map((task, index) => {
                return (
                  <ListCard
                    data={task.title}
                    editIcon={edit_templates}
                    link={
                      edit_templates
                        ? `/${ModulePrefixes.SITE_TEMPLATE}/library-item/tasks/${task.id}`
                        : ""
                    }
                    strictLink={true}
                    secondaryData={
                      isEqual(task.system_status, 1)
                        ? t("module.sourceType.system")
                        : ""
                    }
                    deleteIcon={true}
                    showModalonCross
                    onCross={() => {
                      removeTaskMutation.mutate(task.id);
                    }}
                    crossIcon={remove_items_from_site_template}
                    icon={
                      <ListIconWrapper>
                        <TaskIcon />
                      </ListIconWrapper>
                    }
                    deleteTitle={t(
                      "siteSetting.libraryTemplate.removeTemplate.title"
                    )}
                    deleteButtonText={t(
                      "siteSetting.libraryTemplate.removeTemplate.button"
                    )}
                    deleteMessage={t(
                      "siteSetting.libraryTemplate.removeTemplate.message"
                    )}
                  />
                );
              })
            )
          )}
        </BodyContainer>
      </RightMainContainer>
    </>
  );
}
